import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import BreadcrumbComponent from "../../../../core/components/breadcrumb/breadcrumb.component";
import ContentHeader from "../../../../shared/content-header/content-header.component";
import SkeletonComponent from "../../../../shared/custom-spinner/skeleton.component";
import { safeAtob } from "../../../../shared/helpers/enode-decode-string";
import { BreadCrumbInfoModel } from "../../../../shared/models/breadCrumbInfo.model";
import { GetAllFloorsByBlockCodeWithFaultCount, getFloorPlanOfflineCount } from "../../../../store/slices/dashboard.slice";
import { getLocationBlockByCode } from "../../../../store/slices/location-block.slice";
import DefaultList from "./dashboard-floorplan-defaultlist";
import DragDropList from "./dashboard-floorplan-dragdroplist";
import DashboardFloorplanEmptyFloorPage from "./dashboard-floorplan-empty-floor-page.component";
import HasPermission from "src/core/guards/has-permission";
import { Helmet } from "react-helmet-async";

const DashboardFloorPlanComponent = () => {
  const [PageNumber, setPageNumber] = useState<number>(1);
  const [initialPageState, setInitialPageState] = useState<number | null>(null);
  const PageSize = 25;
  const location = useLocation();
  const [componentRenderCount, setComponentRenderCount] = useState<number>(0);

  const queryParams = new URLSearchParams(location?.search);

  const query = queryParams.get("update");

  const blockDetail = useSelector(
    (state: any) => state.locationBlock.locationBlockDetails
  );
  const isLoadingLocationBlockDetails = useSelector(
    (state: any) => state.locationBlock.isLoadingLocationBlockDetails
  );

  const [dataItems, setDataItems] = useState<any>([]);
  const [numberOfRecords, setNumberOfRecords] = useState<number>(0);
  const [allFloorState, setAllFloorState] = useState<any>([]);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [isNextCall, setisNextCall] = useState<boolean>(true);
  const [isMax, setIsMax] = useState<boolean>(false);

  const [breadCrumbInfo, setBreadCrumbInfo] = useState<BreadCrumbInfoModel[]>(
    []
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const navigateTo = (url: string) => {
    if (url) navigate(url);
  };

  const params: any = useParams();
  const blockCode: any = params.blockCode ? safeAtob(params.blockCode) : null;
  const floor = params?.floor;

  const isLoadingFloors = useSelector(
    (state: any) => state.dashboard.isLoadingFloors
  );

  const assetRemovedOrUpdated = useSelector(
    (state: any) => state.dashboard.assetRemovedOrUpdated
  );

  const mergeOfflineFaultCount = (
    items: any[],
    offlineCount: any[]
  ) => {
    const offlineCountMap = new Map(
      offlineCount.map((item) => [item.Id, item.OfflineCount])
    );

    return items.map((item) => ({
      ...item,
      OfflineFaultCount: offlineCountMap.get(item.Id) ?? 0,
    }));
  };

  useEffect(() => {
    getFloor(1);
  }, [assetRemovedOrUpdated]);

  const getFloor = async (pageNum?: number) => {
    const currentPageNum = pageNum || PageNumber;

    try {
      const [allFlData, offlineCountData] = await Promise.all([
        dispatch(
          GetAllFloorsByBlockCodeWithFaultCount({
            blockCode,
            PageNumber: currentPageNum,
            PageSize,
          })
        ),
        dispatch(getFloorPlanOfflineCount({
          blockCode,
          PageNumber: currentPageNum,
          PageSize,
        }))
      ]);

      const items = allFlData?.payload?.data || [];
      const offlineCount = offlineCountData?.payload?.data || [];

      const mergedItems = mergeOfflineFaultCount(items, offlineCount);

      setAllFloorState(mergedItems);
      setNumberOfRecords(allFlData?.payload?.numberOfRecords);
      setDataItems(mergedItems);

      setPageNumber(currentPageNum + 1);

    } catch (error) {
      console.error("Error fetching floor data:", error);
    }
  };

  useEffect(() => {
    if (dataItems?.length < numberOfRecords) {
      setHasMore(true);
    } else {
      setHasMore(false);
    }
  }, [dataItems, numberOfRecords]);

  /*   const socketRealTimeUpdateData = useSelector((state: any) => {
    return state.asset.socketRealTimeUpdateData;
  }); */

  /*   useEffect(() => {
    if (
      socketRealTimeUpdateData?.BlockCode === blockCode &&
      socketRealTimeUpdateData?.FloorCode === floor
    ) {
      let page: number = 0;

      if (PageNumber > 1) {
        page = PageNumber - 1;
      } else {
        page = PageNumber;
      }

      dispatch(
        GetAllFloorsByBlockCodeWithFaultCount({
          blockCode,
          PageNumber: page,
          PageSize,
        })
      ).then((allFlData: any) => {
        let items = allFlData?.payload?.data;
        setAllFloorState(items);
        setNumberOfRecords(allFlData?.payload?.numberOfRecords);
        setDataItems(items);

        setPageNum(page + 1);
      });
    }
  }, [socketRealTimeUpdateData]); */

  useEffect(() => {
    if (blockCode) {
      getFloor();
    }
  }, [blockCode]);

  useEffect(() => {
    const regexPattern = /^\/dashboard\/[^/]+\/floorplan$/;

    if (dataItems?.length && dataItems[0]?.FloorCode) {
      const { pathname } = location;
      const match = regexPattern.exec(pathname);
      if (match) {
        navigateToUrl(
          `${pathname}/${encodeURIComponent(dataItems[0].FloorCode)}`,
          true
        );
      }
    }
  }, [dataItems]);

  const navigateToUrl = async (
    url: any,
    isReplaced: boolean = false,
    isClicked: boolean = false
  ) => {
    navigate(url, { replace: isReplaced });

    if (!isClicked) return;

    let page: number = 0;

    if (PageNumber > 1) {
      page = PageNumber - 1;
    } else {
      page = PageNumber;
    }

    try {
      const [allFlData, offlineCountData] = await Promise.all([
        dispatch(
          GetAllFloorsByBlockCodeWithFaultCount({
            blockCode,
            PageNumber: 1,
            PageSize: PageSize * page,
          })
        ),
        dispatch(getFloorPlanOfflineCount({
          blockCode,
          PageNumber: 1,
          PageSize: PageSize * page,
        }))
      ]);

      const items = allFlData?.payload?.data || [];
      const offlineCount = offlineCountData?.payload?.data || [];

      const mergedItems = mergeOfflineFaultCount(items, offlineCount);

      setAllFloorState(mergedItems);
      setNumberOfRecords(allFlData?.payload?.numberOfRecords);
      setDataItems(mergedItems);

    } catch (error) {
      console.error("error fetching data:", error);
    }
  };

  const setBreadCrumb = (detailedBlock: any) => {
    setBreadCrumbInfo([
      {
        label: "Dashboard",
        active: false,
        onClick: () => navigateTo("/dashboard"),
      },
      {
        label: `${detailedBlock?.blockName}, ${detailedBlock?.street}, ${detailedBlock?.siteName}, ${detailedBlock?.blockCode}`,
        active: false,
        onClick: () => navigateTo(`/dashboard/${params?.blockCode}`),
      },
      {
        label: floor || "Floor",
        active: true,
      },
    ]);
  };

  useEffect(() => {
    dispatch(getLocationBlockByCode({ blockCode })).then((data: any) => {
      const detailedBlock = data?.payload;
      setBreadCrumb(detailedBlock);
    });
  }, [blockCode]);

  useEffect(() => {
    if (blockDetail && Object.keys(blockDetail).length) {
      setBreadCrumb(blockDetail);
    }
  }, [floor]);

  const fetchMoreData = async () => {
    if (PageNumber <= 1) return;

    let maxData = 500;

    if (dataItems?.length >= maxData) {
      setHasMore(false);
      setisNextCall(false);
      return;
    }

    if (dataItems?.length === 0) return;

    if (!isNextCall) {
      return;
    }

    setisNextCall(false);

    let currentPage: number;

    if (initialPageState) {
      currentPage = initialPageState + 1;
    } else {
      currentPage = PageNumber;
    }

    try {
      const [allFlData, offlineCountData] = await Promise.all([
        dispatch(
          GetAllFloorsByBlockCodeWithFaultCount({
            blockCode,
            PageNumber: currentPage,
            PageSize,
          })
        ),
        dispatch(getFloorPlanOfflineCount({
          blockCode,
          PageNumber: currentPage,
          PageSize,
        }))
      ]);

      const newItems = allFlData?.payload?.data || [];
      const newOfflineCount = offlineCountData?.payload?.data || [];

      const newMergedItems = mergeOfflineFaultCount(newItems, newOfflineCount);
      const totalMergedItems = allFloorState.concat(newMergedItems);

      if (totalMergedItems?.length >= allFlData?.payload?.numberOfRecords) {
        setIsMax(true);
      } else {
        setIsMax(false);
      }

      setAllFloorState(totalMergedItems);
      setDataItems(totalMergedItems);

      setNumberOfRecords(allFlData?.payload?.numberOfRecords);

      setPageNumber(currentPage + 1);
      if (initialPageState) {
        setInitialPageState(initialPageState + 1);
      }

      setisNextCall(true);

    } catch (error) {
      console.error("Error fetching more data:", error);
    };
  }

  useEffect(() => {
    if (isNextCall && !isMax) {
      setHasMore(true);
    }
  }, [isNextCall]);

  useEffect(() => {
    return setInitialPageState(null);
  }, []);

  // Function to completely reset the component's state and trigger re-render
  const resetComponent = () => {
    setInitialPageState(1);

    setDataItems([]);
    setNumberOfRecords(0);
    setAllFloorState([]);
    setHasMore(true);
    setisNextCall(true);
    setIsMax(false);
    setBreadCrumbInfo([]);

    if (blockCode) {
      getFloor(1);
      dispatch(getLocationBlockByCode({ blockCode })).then((data: any) => {
        const detailedBlock = data?.payload;
        setBreadCrumb(detailedBlock);
      });
    }

    setComponentRenderCount((pre: number) => pre + 1);
  };

  const resolveStatusValue = useSelector(
    (state: any) => state.dashboard.resolveStatusValue
  );

  useEffect(() => {
    if (resolveStatusValue) {
      setTimeout(() => {
        resetComponent();
      }, 1000);
    }
  }, [resolveStatusValue]);

  // Responsive margin state
  const [leftMargin, setleftMargin] = useState(
    window.innerWidth > 1200 ? "1rem" : "0rem"
  );

  useEffect(() => {
    const handleResize = () => {
      setleftMargin(window.innerWidth > 1200 ? "1rem" : "0rem");
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <>
      <Helmet>
        <title>
          {blockDetail?.blockName
            ? `${blockDetail?.blockName} | Floor Plan`
            : "Floor Plan | Dashboard"}
        </title>
      </Helmet>
      <div key={componentRenderCount}>
        <div className="">
          <div className="header-container">
            <div className="title-container">
              <SkeletonComponent
                wrapperHeight="90px"
                skeletonHeight="50px"
                isLoading={isLoadingLocationBlockDetails}
              >
                {Object.keys(blockDetail).length ? (
                  <ContentHeader
                    pageTitle={
                      floor
                        ? `${blockDetail?.blockName}, ${blockDetail?.street}, ${blockDetail?.siteName}, ${blockDetail?.blockCode} - ${floor}`
                        : `${blockDetail?.blockName}, ${blockDetail?.street}, ${blockDetail?.siteName}, ${blockDetail?.blockCode}`
                    }
                  />
                ) : null}
              </SkeletonComponent>
            </div>
          </div>

          <div className="page">
            <div className="page-container mt-3">
              <SkeletonComponent
                wrapperHeight="30px"
                skeletonHeight="20px"
                isLoading={isLoadingLocationBlockDetails}
              >
                {!!breadCrumbInfo?.length && (
                  <BreadcrumbComponent breadCrumbInfo={breadCrumbInfo} />
                )}
              </SkeletonComponent>

              {dataItems?.length === 0 && !isLoadingLocationBlockDetails && !isLoadingFloors ? (
                <DashboardFloorplanEmptyFloorPage
                  resetComponent={resetComponent}
                />
              ) : (
                <div className="d-flex flex-xl-row flex-column justify-content-between  gap-3">
                  {query ? (
                    <HasPermission
                      moduleName="Floor Plan Detail"
                      actionName="Update-Floor Plan Detail"
                      isRoute={false}
                    >
                      <DragDropList
                        key={query}
                        setDataItems={setDataItems}
                        dataItems={dataItems}
                        // isLoadingFloors={isLoadingFloors}
                        hasMore={hasMore}
                        // setFloorNameState={setFloorNameState}
                        // setAllFloorState={setAllFloorState}
                        fetchMoreData={fetchMoreData}
                        navigateToUrl={navigateToUrl}
                        resetComponent={resetComponent}
                      />
                    </HasPermission>
                  ) : (
                    <DefaultList
                      dataItems={dataItems}
                      isLoadingFloors={isLoadingFloors}
                      hasMore={hasMore}
                      fetchMoreData={fetchMoreData}
                      navigateToUrl={navigateToUrl}
                    />
                  )}

                  <div
                    className="w-lg-75 bg-card shadow-sm z-1"
                    style={{ marginLeft: leftMargin }}
                  >
                    <Outlet />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DashboardFloorPlanComponent;
